import { all, call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";
import { IUnitAnalytics } from "../App/Analytics/Common";
import { api } from "../dependencies";
import {
  analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData,
  analyticsCourseFullGetResponseToUnits,
  analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData,
} from "../parsers/analytics";
import { SagaAction } from "../sagas/types";
import * as selectors from "../selectors/selectors";
import { SagaFunction } from "./types";

export interface IFetchUsersAnalyticsSummaryFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const fetchUsersAnalyticsSummaryForClass: (
  furtherActions: IFetchUsersAnalyticsSummaryFurtherActions
) => SagaFunction = furtherActions =>
  function* _fetchUsersAnalyticsSummary(action: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const [setError, setResponse] = yield call(
      api.analytics.getClassSummarySet,
      accountId,
      action.courseId,
      action.classId
    );
    if (setError) {
      yield put(furtherActions.onFailure({ message: "Failed to fetch analytics set." }));
    } else {
      const setId = setResponse.data.summarySetId;
      yield put;
      const { courseInstancesResult, compactUnitResult } = yield all({
        compactUnitResult: call(
          api.analytics.getClassCompactUnitSummaries,
          accountId,
          action.courseId,
          action.classId,
          setId
        ),
        courseInstancesResult: call(
          api.analytics.getClassCourseInstancesSummaries,
          accountId,
          action.courseId,
          action.classId,
          setId
        ),
      });
      const [courseInstancesError, courseInstancesResponse, compactUnitError, compactUnitResponse] = [
        ...courseInstancesResult,
        ...compactUnitResult,
      ];
      if (courseInstancesError) {
        yield put(
          furtherActions.onFailure({
            message: "Failed to fetch course instance analytics.",
          })
        );
      } else if (compactUnitError) {
        yield put(
          furtherActions.onFailure({
            message: "Failed to fetch compact unit analytics.",
          })
        );
      } else {
        const compactUnitData = analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(
          compactUnitResponse.data
        );
        const courseInstanceData = analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData(
          courseInstancesResponse.data
        );
        const userAnalytics = {};
        Object.entries(courseInstanceData).forEach(([k, v]) => {
          userAnalytics[k] = { ...v, ...compactUnitData[v.unitSummaryId] };
        });
        const [fullCourseResponseError, fullCourseResponse] = yield call(api.resolve.courseFull.post, accountId, [
          action.courseId,
        ]);
        if (fullCourseResponseError) {
          yield put(
            furtherActions.onFailure({
              message: "Failed to fetch full course.",
            })
          );
        } else {
          const unitNames = analyticsCourseFullGetResponseToUnits(
            fullCourseResponse.data[0].value,
            fullCourseResponseError
          );
          const unitRollupTasks = unitNames.map(unit =>
            call(
              api.analytics.getClassUnitRollup,
              accountId,
              action.courseId,
              action.classId,
              setResponse.data.summarySetId,
              unit.index
            )
          );
          const unitRollupResults = yield all(unitRollupTasks);
          if (unitRollupResults.find(([unitRollupError]: [any]) => unitRollupError)) {
            yield put(
              furtherActions.onFailure({
                message: "Failed to fetch unit rollup.",
              })
            );
          } else {
            const unitUuidToAccountUuid = {};
            Object.entries(userAnalytics).forEach(([k, v]: any) => (unitUuidToAccountUuid[v.unitSummaryId] = k));
            const unitsAnalytics: IUnitAnalytics[] = [];
            unitRollupResults.forEach(([_, resp]: [any, any], i: number) => {
              const analytics = {};
              Object.entries(analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(resp.data)).forEach(
                ([k, v]) => {
                  analytics[unitUuidToAccountUuid[k]] = v;
                }
              );
              const unitAnalytics = {
                analytics,
                ...unitNames[i],
              } as any;
              unitsAnalytics.push(unitAnalytics);
            });
            yield put(
              furtherActions.onSuccess(
                userAnalytics,
                unitsAnalytics,
                api.analytics.getClassPartInstSummaryRollup(accountId, action.courseId, action.classId, setId)
              )
            );
          }
        }
      }
    }
  };

export const fetchUsersAnalyticsSummaryForScormPackage: (
  furtherActions: IFetchUsersAnalyticsSummaryFurtherActions
) => SagaFunction = furtherActions =>
  function* _fetchUsersAnalyticsSummary(action: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const [setError, setResponse] = yield call(
      api.analytics.getScormPackageSummarySet,
      accountId,
      action.courseId,
      action.scormPackageId
    );
    if (setError) {
      yield put(furtherActions.onFailure({ message: "Failed to fetch analytics set." }));
    } else {
      const setId = setResponse.data.summarySetId;
      const { courseInstancesResult, compactUnitResult } = yield all({
        compactUnitResult: call(
          api.analytics.getScormPackageCompactUnitSummaries,
          accountId,
          action.courseId,
          action.scormPackageId,
          setId
        ),
        courseInstancesResult: call(
          api.analytics.getScormPackageCourseInstancesSummaries,
          accountId,
          action.courseId,
          action.scormPackageId,
          setId
        ),
      });
      const [courseInstancesError, courseInstancesResponse, compactUnitError, compactUnitResponse] = [
        ...courseInstancesResult,
        ...compactUnitResult,
      ];
      if (courseInstancesError) {
        yield put(
          furtherActions.onFailure({
            message: "Failed to fetch course instance analytics.",
          })
        );
      } else if (compactUnitError) {
        yield put(
          furtherActions.onFailure({
            message: "Failed to fetch compact unit analytics.",
          })
        );
      } else {
        const compactUnitData = analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(
          compactUnitResponse.data
        );
        const courseInstanceData = analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData(
          courseInstancesResponse.data
        );
        const userAnalytics = {};
        Object.entries(courseInstanceData).forEach(([k, v]) => {
          userAnalytics[k] = { ...v, ...compactUnitData[v.unitSummaryId] };
        });
        const [fullCourseResponseError, fullCourseResponse] = yield call(api.resolve.courseFull.post, accountId, [
          action.courseId,
        ]);
        if (fullCourseResponseError) {
          yield put(
            furtherActions.onFailure({
              message: "Failed to fetch full course.",
            })
          );
        } else {
          const unitNames = analyticsCourseFullGetResponseToUnits(
            fullCourseResponse.data[0].value,
            fullCourseResponseError
          );
          const unitRollupTasks = unitNames.map(unit =>
            call(
              api.analytics.getScormPackageUnitRollup,
              accountId,
              action.scormPackageId,
              setResponse.data.summarySetId,
              unit.index
            )
          );
          const unitRollupResults = yield all(unitRollupTasks);
          if (unitRollupResults.find(([unitRollupError]: [any]) => unitRollupError)) {
            yield put(
              furtherActions.onFailure({
                message: "Failed to fetch unit rollup.",
              })
            );
          } else {
            const unitUuidToAccountUuid = {};
            Object.entries(userAnalytics).forEach(([k, v]: any) => (unitUuidToAccountUuid[v.unitSummaryId] = k));
            const unitsAnalytics: IUnitAnalytics[] = [];
            for (let i = 0; i < unitRollupResults.length; i++) {
              const [, resp] = unitRollupResults[i];
              const analytics = {};
              Object.entries(analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(resp.data)).forEach(
                ([k, v]) => {
                  analytics[unitUuidToAccountUuid[k]] = v;
                }
              );
              const unitAnalytics = {
                analytics,
                ...unitNames[i],
              } as any;
              unitsAnalytics.push(unitAnalytics);
            }
            yield put(
              furtherActions.onSuccess(
                userAnalytics,
                unitsAnalytics,
                api.analytics.getScormPackagePartInstSummaryRollup(accountId, action.scormPackageId, setId)
              )
            );
          }
        }
      }
    }
  };
